import axios, { type AxiosInstance, type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useAdminAuthStore } from '../store/authStore';
import type { Session } from '../types/common';
import { ApiValidationError, SystemError } from '../lib/errors';
import { generateReferenceId } from '../store/errorStore';

const API_URL = import.meta.env.VITE_API_URL as string;

interface RetryableRequest extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _requestId?: string;
  _referenceId?: string;
}

class TokenRefreshManager {
  private refreshing = false;
  private queue: Array<(token: string) => void> = [];

  async refresh(
    accessToken: string,
    refreshToken: string,
    onUpdate: (session: Session) => void,
    onLogout: () => void
  ): Promise<string> {
    if (this.refreshing) {
      return new Promise((resolve) => { this.queue.push(resolve); });
    }
    this.refreshing = true;
    try {
      const res = await axios.post(`${API_URL}/auth/refresh`, { token: accessToken, refreshToken });
      const raw = res.data.data;
      const newAccessToken = raw.newAccessToken;
      const decoded = jwtDecode<{ exp: number }>(newAccessToken);
      const newSession: Session = {
        ...raw,
        accessToken: newAccessToken,
        refreshToken: raw.newRefreshToken,
        expiresAt: decoded.exp * 1000,
        refreshTokenExpiresAt: new Date(raw.refreshTokenExpiryTime).getTime(),
      };
      onUpdate(newSession);
      this.queue.forEach((cb) => cb(newAccessToken));
      this.queue = [];
      return newSession.accessToken;
    } catch {
      onLogout();
      this.queue = [];
      throw new Error('Session expired. Please log in again.');
    } finally {
      this.refreshing = false;
    }
  }
}

const tokenManager = new TokenRefreshManager();

function createAdminAxios(): AxiosInstance {
  const instance = axios.create({
    baseURL: API_URL,
    timeout: 60000,
    headers: { 'Content-Type': 'application/json' },
  });

  instance.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const { session } = useAdminAuthStore.getState();

    // Attach correlation IDs on every request regardless of auth state.
    const requestId = crypto.randomUUID();
    const referenceId = generateReferenceId();
    config.headers['X-Request-Id'] = requestId;
    config.headers['X-Reference-Id'] = referenceId;
    // Platform admins have no institute; this tells the API to skip the institute check and still load their permissions.
    config.headers['X-App-Source'] = 'AdminPanel';
    (config as RetryableRequest)._requestId = requestId;
    (config as RetryableRequest)._referenceId = referenceId;

    if (!session) return config;

    const nowMs = Date.now();
    const expiresInMs = session.expiresAt - nowMs;

    if (expiresInMs < 60_000) {
      try {
        const token = await tokenManager.refresh(
          session.accessToken,
          session.refreshToken,
          (s) => useAdminAuthStore.getState().updateSession(s),
          () => useAdminAuthStore.getState().logout()
        );
        config.headers.Authorization = `Bearer ${token}`;
      } catch {
        useAdminAuthStore.getState().logout();
        return config;
      }
    } else {
      config.headers.Authorization = `Bearer ${session.accessToken}`;
    }

    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      if (response.data && response.data.succeeded === false) {
        const messages: string[] = response.data.messages?.length
          ? response.data.messages
          : [response.data.message || 'Request failed'];
        return Promise.reject(new ApiValidationError(messages));
      }
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config as RetryableRequest;

      // 401: attempt token refresh once then retry
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        const { session } = useAdminAuthStore.getState();
        if (session?.refreshToken) {
          try {
            const token = await tokenManager.refresh(
              session.accessToken,
              session.refreshToken,
              (s) => useAdminAuthStore.getState().updateSession(s),
              () => useAdminAuthStore.getState().logout()
            );
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return instance(originalRequest);
          } catch {
            useAdminAuthStore.getState().logout();
          }
        }
      }

      // 400: surface backend validation messages if present
      if (error.response?.status === 400) {
        const data = error.response.data as Record<string, unknown> | undefined;
        const messages = data?.messages as string[] | undefined;
        if (messages?.length) {
          return Promise.reject(new ApiValidationError(messages));
        }
        return Promise.reject(new Error('Bad request. Please check input values.'));
      }

      // All other errors: throw SystemError with correlation IDs
      const requestId = originalRequest?._requestId;
      const referenceId = originalRequest?._referenceId;
      const responseData = error.response?.data as Record<string, unknown> | undefined;
      const backendTraceId = responseData?.traceId as string | undefined;
      const rawMessage = (error as Error).message || 'Unexpected error';

      return Promise.reject(
        new SystemError(rawMessage, { requestId, referenceId, backendTraceId, statusCode: error.response?.status })
      );
    }
  );

  return instance;
}

export const axiosInstance = createAdminAxios();

interface LoginResponse {
  token: string;
  refreshToken: string;
  refreshTokenExpiryTime: string;
  permissions: string[];
  forcePasswordReset: boolean;
}

export async function adminLogin(username: string, password: string): Promise<Session> {
  const res = await axios.post(`${API_URL}/auth/Login`, {
    username,
    hashedPassword: password,
    userType: 'Admin',
  });

  if (!res.data?.succeeded) {
    throw new Error(res.data?.messages?.[0] ?? 'Login failed');
  }

  const data: LoginResponse = res.data.data;
  if (!data?.token) throw new Error('Invalid response from server');

  const decoded = jwtDecode<{
    sub: string;
    email: string;
    firstName: string;
    lastName: string;
    username: string;
    roles?: string | string[];
    exp: number;
  }>(data.token);

  const roles = Array.isArray(decoded.roles)
    ? decoded.roles
    : decoded.roles
    ? [decoded.roles]
    : [];

  if (!roles.includes('SystemAdmin')) {
    throw new Error('Access denied. SystemAdmin role required.');
  }

  const session: Session = {
    accessToken: data.token,
    refreshToken: data.refreshToken,
    expiresAt: decoded.exp * 1000,
    refreshTokenExpiresAt: new Date(data.refreshTokenExpiryTime).getTime(),
    user: {
      id: decoded.sub,
      firstName: decoded.firstName,
      lastName: decoded.lastName,
      email: decoded.email,
      userName: decoded.username,
      roles,
      permissions: data.permissions ?? [],
    },
  };

  return session;
}
